import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaExternalLinkAlt, FaImage, FaClipboardCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';
import AnimatedModal from './AnimatedModal';
import ModalHelp from '../../../components/ModalHelp';
import FinalizarIntegracaoCandidatosModal from './FinalizarIntegracaoCandidatosModal';
import FinalizarIntegracaoFotosModal from './FinalizarIntegracaoFotosModal';
import api from '../../../api';

interface ConfirmarIntegracaoModalProps {
  banca: any;
  processo: any;
  onClose: () => void;
  onConfirm: () => void;
}

interface EstatisticasCandidatos {
  total: number;
  comResultado: number;
  semResultado: number;
  comFoto: number;
  semFoto: number;
  totalProcesso: number;
  comFotoProcesso: number;
  semFotoProcesso: number;
}

const ConfirmarIntegracaoModal: React.FC<ConfirmarIntegracaoModalProps> = ({
  banca,
  processo,
  onClose,
  onConfirm
}) => {
  const [stats, setStats] = useState<EstatisticasCandidatos | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [showFinalModal, setShowFinalModal] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get(`/bancas/${banca.id}/estatisticas-candidatos`);
        setStats(response.data);
      } catch (error) {
        console.error('Erro ao buscar estatísticas:', error);
        toast.error('Erro ao carregar estatísticas dos candidatos');
      } finally {
        setLoadingStats(false);
      }
    };
    fetchStats();
  }, [banca.id]);

  const handleProsseguir = () => {
    setShowFinalModal(true);
  };

  const getStatusText = (com: number, total: number) => {
    if (total === 0) return 'Nenhum candidato';
    if (com === total) return 'Todos';
    if (com === 0) return 'Nenhum';
    return 'Alguns';
  };

  const getRevisaoUrl = () => {
    return `/processos/${processo?.id}/bancas/${banca.id}/candidatos`;
  };

  const todosTemResultado = stats && stats.total > 0 && stats.comResultado === stats.total;

  if (showFinalModal) {
    if (todosTemResultado) {
      return <FinalizarIntegracaoCandidatosModal banca={banca} processo={processo} onClose={onClose} onConfirm={onConfirm} />;
    } else {
      return <FinalizarIntegracaoFotosModal banca={banca} processo={processo} onClose={onClose} onConfirm={onConfirm} />;
    }
  }

  return (
    <AnimatedModal isOpen={true} onClose={onClose}>
      <div style={{
        backgroundColor: 'white', padding: '32px', borderRadius: '12px',
        width: '600px', maxWidth: '90vw', maxHeight: '80vh',
        overflow: 'auto', boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '48px', height: '48px', borderRadius: '50%',
            backgroundColor: '#dbeafe', marginBottom: '12px'
          }}>
            <FaCheckCircle style={{ color: '#2563eb', fontSize: '20px' }} />
          </div>
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
            Concluir Integração
        <ModalHelp text="Conclui a integração da banca. Após a conclusão, os resultados ficam disponíveis definitivamente para os sistemas integrados." />
          </h3>
        </div>

        <div style={{
          backgroundColor: '#f0f9ff', padding: '16px', borderRadius: '8px',
          marginBottom: '20px', border: '1px solid #0ea5e9'
        }}>
          <div style={{ fontSize: '14px', color: '#0c4a6e', lineHeight: '1.5' }}>
            Revise as estatísticas abaixo antes de prosseguir com a finalização da integração.
          </div>
        </div>

        {processo && (
          <div style={{
            backgroundColor: '#f8f9fa', padding: '16px', borderRadius: '8px',
            marginBottom: '20px', border: '1px solid #e9ecef'
          }}>
            <div style={{ marginBottom: '12px' }}>
              <strong>Processo:</strong> {processo.nome}
            </div>
            <a
              href={getRevisaoUrl()}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#2563eb', textDecoration: 'none',
                display: 'flex', alignItems: 'center', gap: '6px',
                fontSize: '14px', fontWeight: '500'
              }}
            >
              <FaExternalLinkAlt style={{ fontSize: '12px' }} />
              Clique aqui para revisar a lista de candidatos enviados por Integração
            </a>
          </div>
        )}

        {loadingStats ? (
          <div style={{
            backgroundColor: '#f8f9fa', padding: '16px', borderRadius: '8px',
            marginBottom: '20px', textAlign: 'center', color: '#6b7280'
          }}>
            Carregando estatísticas...
          </div>
        ) : stats && (
          <div style={{
            backgroundColor: '#f0f9ff', padding: '16px', borderRadius: '8px',
            marginBottom: '20px', border: '1px solid #0ea5e9'
          }}>
            <div style={{ marginBottom: '12px', fontWeight: '600', color: '#0c4a6e' }}>
              Estatísticas dos Candidatos da Banca:
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                backgroundColor: 'white', padding: '12px', borderRadius: '6px',
                border: '1px solid #bae6fd'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <FaClipboardCheck style={{ color: '#0ea5e9', fontSize: '16px' }} />
                  <strong style={{ fontSize: '14px', color: '#0c4a6e' }}>Resultados</strong>
                </div>
                <div style={{ fontSize: '13px', color: '#475569' }}>
                  <div><strong>{getStatusText(stats.comResultado, stats.total)}</strong> têm resultado</div>
                  <div style={{ marginTop: '4px', fontSize: '12px', color: '#64748b' }}>
                    {stats.comResultado} de {stats.total} candidatos
                  </div>
                </div>
              </div>
              <div style={{
                backgroundColor: 'white', padding: '12px', borderRadius: '6px',
                border: '1px solid #bae6fd'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <FaImage style={{ color: '#0ea5e9', fontSize: '16px' }} />
                  <strong style={{ fontSize: '14px', color: '#0c4a6e' }}>Fotos da Banca</strong>
                </div>
                <div style={{ fontSize: '13px', color: '#475569' }}>
                  <div><strong>{getStatusText(stats.comFoto, stats.total)}</strong> têm foto</div>
                  <div style={{ marginTop: '4px', fontSize: '12px', color: '#64748b' }}>
                    {stats.comFoto} de {stats.total} candidatos
                  </div>
                </div>
              </div>
            </div>
            <div style={{ borderTop: '1px solid #bae6fd', paddingTop: '12px' }}>
              <div style={{ marginBottom: '8px', fontWeight: '600', color: '#0c4a6e', fontSize: '13px' }}>
                Fotos do Processo:
              </div>
              <div style={{
                backgroundColor: 'white', padding: '12px', borderRadius: '6px',
                border: '1px solid #bae6fd'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <FaImage style={{ color: '#0ea5e9', fontSize: '16px' }} />
                  <strong style={{ fontSize: '14px', color: '#0c4a6e' }}>Todos os Candidatos</strong>
                </div>
                <div style={{ fontSize: '13px', color: '#475569' }}>
                  <div><strong>{getStatusText(stats.comFotoProcesso, stats.totalProcesso)}</strong> têm foto</div>
                  <div style={{ marginTop: '4px', fontSize: '12px', color: '#64748b' }}>
                    {stats.comFotoProcesso} de {stats.totalProcesso} candidatos do processo
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px', border: '1px solid #d1d5db',
              backgroundColor: 'white', borderRadius: '6px',
              cursor: 'pointer', fontSize: '14px', fontWeight: '500'
            }}
            disabled={loadingStats}
          >
            Cancelar
          </button>

          <button
            onClick={handleProsseguir}
            disabled={loadingStats}
            style={{
              padding: '10px 20px', backgroundColor: '#2563eb',
              color: 'white', border: 'none', borderRadius: '6px',
              cursor: loadingStats ? 'not-allowed' : 'pointer',
              fontSize: '14px', fontWeight: '500',
              opacity: loadingStats ? 0.6 : 1,
              display: 'flex', alignItems: 'center', gap: '6px'
            }}
          >
            <FaCheckCircle style={{ fontSize: '12px' }} />
            Prosseguir
          </button>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default ConfirmarIntegracaoModal;
